import { CameraModeType } from "@features/camera/modes";
import { useCreateImage } from "@features/images/use-create-image";
import { usePacks } from "@features/images/use-packs";
import { useEffectPlease } from "@features/utils";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

// Type definition for form state
type CameraFormState = {
  mode: CameraModeType | "none";
  model: string;
  style: string;
  pack: string;
  image: File | string | null;
  similarity: number;
  project: string;
  promptTxt: string;
  format: string;
  count: string;
  loading: boolean;
};

// Context type
type CameraFormContextType = {
  // State values
  mode: CameraModeType | "none";
  model: string;
  style: string;
  pack: string;
  image: File | string | null;
  similarity: number;
  project: string;
  promptTxt: string;
  format: string;
  count: string;
  loading: boolean;

  // Setters
  setMode: (mode: CameraModeType | "none") => void;
  setModel: (model: string) => void;
  setStyle: (style: string) => void;
  setPack: (pack: string) => void;
  setProjectId: (project: string) => void;
  setPromptTxt: (promptTxt: string) => void;
  setFormat: (format: string) => void;
  setCount: (count: string) => void;
  setSimilarity: (similarity: number) => void;
  setFromImage: (image: File | string | null) => void;
  setLoading: (loading: boolean) => void;

  // Derived values and utilities
  effectiveCount: string;
  formats: Record<string, string>;
  packs: any[];
  handleFormSubmit: () => Promise<void>;
  refetchFromUrl: () => void;
};

// Create context
const CameraFormContext = createContext<CameraFormContextType | undefined>(
  undefined
);

// Helper function to get initial data from URL and localStorage
const getInitialData = (): CameraFormState => {
  const fromUrl = new URLSearchParams(window.location.search);
  const fromStorage = JSON.parse(localStorage.getItem("camera-form") || "{}");

  return {
    mode: (fromUrl.get("mode") ||
      fromStorage.mode ||
      "pack") as CameraFormState["mode"],
    model: fromUrl.get("model") || fromStorage.model || "none",
    style: fromUrl.get("style") || fromStorage.style || "none",
    pack: fromUrl.get("pack") || fromStorage.pack || "none",
    image: fromUrl.get("image") || null,
    similarity: parseInt(
      fromUrl.get("similarity") || fromStorage.similarity || "20"
    ),
    project: fromUrl.get("project") || fromStorage.project || "none",
    promptTxt: fromUrl.get("prompt") || fromStorage.promptTxt || "",
    format: fromUrl.get("format") || fromStorage.format || "auto",
    count: fromUrl.get("count") || fromStorage.count || "4",
    loading: false,
  };
};

// Helper function to sync state with localStorage and URL
const syncStateWithStorage = (state: Partial<CameraFormState>) => {
  // Update localStorage
  const currentStorage = JSON.parse(
    localStorage.getItem("camera-form") || "{}"
  );
  const newStorage = { ...currentStorage, ...state };
  localStorage.setItem("camera-form", JSON.stringify(newStorage));

  // Update URL without creating new history entry
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Update or remove URL parameters based on state
  Object.entries(state).forEach(([key, value]) => {
    if (value && value !== "none" && key !== "loading") {
      params.set(key, String(value));
    } else if (key !== "loading") {
      params.delete(key);
    }
  });

  // Mark that this change came from the form
  (window as any).__formUpdatingUrl = true;
  window.history.replaceState({}, "", url.toString());
  setTimeout(() => {
    (window as any).__formUpdatingUrl = false;
  }, 100);
};

// Provider component
export const CameraFormProvider = ({
  children,
  onChange,
  onRun,
}: {
  children: ReactNode;
  onChange?: (arg: {
    model: string;
    style: string;
    pack: string;
    project: string;
  }) => void;
  onRun?: () => void;
}) => {
  const { create } = useCreateImage();
  const { t } = useTranslation();
  const { packs: _packs } = usePacks();
  const packs = _packs.data || [];
  const location = useLocation();
  const isInitialMount = useRef(true);

  // Initialize state with data from URL and localStorage
  const initialData = getInitialData();

  // Individual useState hooks for each field
  const [mode, setModeState] = useState<CameraModeType | "none">(
    initialData.mode
  );
  const [model, setModelState] = useState<string>(initialData.model);
  const [style, setStyleState] = useState<string>(initialData.style);
  const [pack, setPackState] = useState<string>(initialData.pack);
  const [image, setImageState] = useState<File | string | null>(
    initialData.image
  );
  const [similarity, setSimilarityState] = useState<number>(
    initialData.similarity
  );
  const [project, setProjectState] = useState<string>(initialData.project);
  const [promptTxt, setPromptTxtState] = useState<string>(
    initialData.promptTxt
  );
  const [format, setFormatState] = useState<string>(initialData.format);
  const [count, setCountState] = useState<string>(initialData.count);
  const [loading, setLoadingState] = useState<boolean>(false);

  // Wrapped setters that sync with storage and URL
  const setMode = useCallback((newMode: CameraModeType | "none") => {
    setModeState(newMode);
    syncStateWithStorage({ mode: newMode });
  }, []);

  const setModel = useCallback((newModel: string) => {
    setModelState(newModel);
    syncStateWithStorage({ model: newModel });
  }, []);

  const setStyle = useCallback((newStyle: string) => {
    setStyleState(newStyle);
    syncStateWithStorage({ style: newStyle });
  }, []);

  const setPack = useCallback((newPack: string) => {
    setPackState(newPack);
    syncStateWithStorage({ pack: newPack });
  }, []);

  const setProjectId = useCallback((newProject: string) => {
    setProjectState(newProject);
    syncStateWithStorage({ project: newProject });
  }, []);

  const setPromptTxt = useCallback((newPromptTxt: string) => {
    setPromptTxtState(newPromptTxt);
    syncStateWithStorage({ promptTxt: newPromptTxt });
  }, []);

  const setFormat = useCallback((newFormat: string) => {
    setFormatState(newFormat);
    syncStateWithStorage({ format: newFormat });
  }, []);

  const setCount = useCallback((newCount: string) => {
    setCountState(newCount);
    syncStateWithStorage({ count: newCount });
  }, []);

  const setSimilarity = useCallback((newSimilarity: number) => {
    setSimilarityState(newSimilarity);
    syncStateWithStorage({ similarity: newSimilarity });
  }, []);

  const setFromImage = useCallback((newImage: File | string | null) => {
    setImageState(newImage);
    // Don't sync image to storage as it might be too large
  }, []);

  const setLoading = useCallback((newLoading: boolean) => {
    setLoadingState(newLoading);
    // Don't sync loading state to storage
  }, []);

  // Derived state
  const effectiveCount = String(
    packs.find((a) => a.id === pack && mode === "pack")?.count || count
  );

  // Formats object
  const formats = {
    auto: t("camera.form.formats.auto"),
    portrait: t("camera.form.formats.portrait"),
    landscape: t("camera.form.formats.landscape"),
    square: t("camera.form.formats.square"),
  };

  // Function to refetch data from URL (for when URL changes externally)
  const refetchFromUrl = useCallback(() => {
    const newData = getInitialData();
    setModeState(newData.mode);
    setModelState(newData.model);
    setStyleState(newData.style);
    setPackState(newData.pack);
    setImageState(newData.image);
    setSimilarityState(newData.similarity);
    setProjectState(newData.project);
    setPromptTxtState(newData.promptTxt);
    setFormatState(newData.format);
    setCountState(newData.count);
  }, []);

  // Handle form submission
  const handleFormSubmit = useCallback(async () => {
    setLoading(true);
    try {
      let fromImageB64 = null;
      if (image) {
        if (typeof image !== "string") {
          const reader = new FileReader();
          reader.readAsDataURL(image as unknown as File);
          fromImageB64 = await new Promise<string>((resolve, reject) => {
            reader.onload = () => {
              if (typeof reader.result === "string") {
                resolve(reader.result);
              } else {
                reject(new Error("FileReader result is not a string"));
              }
            };
            reader.onerror = () => {
              reject(new Error("FileReader encountered an error"));
            };
          });
        } else {
          fromImageB64 = image;
        }
      }

      await create({
        size: format,
        mode: mode,
        prompt: promptTxt || "",
        model_id: model,
        style_id: style,
        pack_id: pack,
        project_id: (project !== "none" ? project : "") || undefined,
        count: parseInt(`${effectiveCount}`),
        from_image: fromImageB64 || undefined,
        similarity: similarity / 100,
      });

      toast.success(
        t("camera.form.image.success", { replace: { count: effectiveCount } })
      );

      // Scroll to id=anchor-camera-roll
      const anchor = document.getElementById("anchor-camera-roll");
      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth" });
      }
    } catch (e) {
      console.error(e);
      toast.error(
        t("camera.form.image.error", { replace: { count: effectiveCount } })
      );
    } finally {
      if (onRun) onRun();
      setLoading(false);
    }
  }, [
    image,
    format,
    mode,
    promptTxt,
    model,
    style,
    pack,
    project,
    effectiveCount,
    similarity,
    create,
    t,
    onRun,
    setLoading,
  ]);

  // Notify parent component of changes (if provided)
  useEffectPlease(() => {
    if (onChange) {
      onChange({
        model: model,
        style: mode !== "pack" ? style : "none",
        pack: mode === "pack" ? pack : "none",
        project: project !== "none" ? project : "none",
      });
    }
  }, [model, pack, style, mode, project, onChange]);

  // Listen for URL changes from navigation (not from form updates)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if the URL was updated by the form itself
    if ((window as any).__formUpdatingUrl) {
      return;
    }

    // URL changed from external navigation, update form state
    const newData = getInitialData();
    setModeState(newData.mode);
    setModelState(newData.model);
    setStyleState(newData.style);
    setPackState(newData.pack);
    setImageState(newData.image);
    setSimilarityState(newData.similarity);
    setProjectState(newData.project);
    setPromptTxtState(newData.promptTxt);
    setFormatState(newData.format);
    setCountState(newData.count);
  }, [location.search]);

  const contextValue: CameraFormContextType = {
    // State values
    mode,
    model,
    style,
    pack,
    image,
    similarity,
    project,
    promptTxt,
    format,
    count,
    loading,

    // Setters
    setMode,
    setModel,
    setStyle,
    setPack,
    setProjectId,
    setPromptTxt,
    setFormat,
    setCount,
    setSimilarity,
    setFromImage,
    setLoading,

    // Derived values and utilities
    effectiveCount,
    formats,
    packs,
    handleFormSubmit,
    refetchFromUrl,
  };

  return (
    <CameraFormContext.Provider value={contextValue}>
      {children}
    </CameraFormContext.Provider>
  );
};

// Hook to use the camera form context
export const useCameraForm = () => {
  const context = useContext(CameraFormContext);
  if (context === undefined) {
    throw new Error("useCameraForm must be used within a CameraFormProvider");
  }
  return context;
};
